import { useState } from "react";
import { Banknote, Check, X } from "lucide-react";
import type { CreditCard } from "../../lib/types";

interface Props {
  cards: CreditCard[];
  onUpdateBalance: (id: string, balance: number) => void;
  onPayClick: (card: CreditCard) => void;
}

const fmt = (n: number) =>
  n.toLocaleString("es-MX", { maximumFractionDigits: 0 });

function UsageBar({ percent, status }: { percent: number; status: string }) {
  const color =
    status === "critical"
      ? "bg-red-500"
      : status === "warning"
        ? "bg-yellow-500"
        : "bg-emerald-500";

  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 rounded-full bg-surface-2 overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-all`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <span
        className={`text-xs ${status === "critical" ? "text-red-400" : status === "warning" ? "text-yellow-400" : "text-gray-400"}`}
      >
        {percent}%
      </span>
    </div>
  );
}

function InlineEdit({
  value,
  onSave,
}: {
  value: number;
  onSave: (v: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(String(value));

  if (!editing) {
    return (
      <button
        className="text-gray-200 hover:text-accent transition-colors text-left"
        onClick={() => {
          setInput(String(value));
          setEditing(true);
        }}
        title="Click para editar"
      >
        ${fmt(value)}
      </button>
    );
  }

  const handleSave = () => {
    const v = parseFloat(input);
    if (!isNaN(v) && v >= 0) onSave(v);
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        className="input w-24 py-0.5 text-xs"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
          if (e.key === "Escape") setEditing(false);
        }}
        autoFocus
      />
      <button onClick={handleSave} className="text-emerald-400 p-0.5">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => setEditing(false)}
        className="text-gray-500 p-0.5"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function CardsTable({ cards, onUpdateBalance, onPayClick }: Props) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-sm font-medium text-gray-300">Tarjetas</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] text-gray-500 uppercase border-b border-border">
              <th className="px-4 py-2 text-left">#</th>
              <th className="px-4 py-2 text-left">Tarjeta</th>
              <th className="px-4 py-2 text-right">Saldo</th>
              <th className="px-4 py-2 text-right">Limite</th>
              <th className="px-4 py-2 text-center">Uso</th>
              <th className="px-4 py-2 text-right">Tasa</th>
              <th className="px-4 py-2 text-right">Interes/Mes</th>
              <th className="px-4 py-2 text-right">Disponible</th>
              <th className="px-4 py-2 text-center">Pago</th>
              <th className="px-4 py-2 text-center">Dias</th>
              <th className="px-4 py-2 text-center">Accion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {cards.map((card) => (
              <tr
                key={card.id}
                className="hover:bg-surface-3 transition-colors"
              >
                <td className="px-4 py-2.5">
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{
                      color: card.color,
                      backgroundColor: `${card.color}15`,
                    }}
                  >
                    {card.priority}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: card.color }}
                    />
                    <span className="text-gray-200 font-medium">
                      {card.name}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <InlineEdit
                    value={card.balance}
                    onSave={(v) => onUpdateBalance(card.id, v)}
                  />
                </td>
                <td className="px-4 py-2.5 text-right text-gray-400">
                  ${fmt(card.credit_limit)}
                </td>
                <td className="px-4 py-2.5">
                  <UsageBar
                    percent={card.usage_percent}
                    status={card.status}
                  />
                </td>
                <td className="px-4 py-2.5 text-right text-gray-400">
                  {card.interest_rate}%
                </td>
                <td className="px-4 py-2.5 text-right text-red-400">
                  ${fmt(card.monthly_interest)}
                </td>
                <td className="px-4 py-2.5 text-right text-emerald-400">
                  ${fmt(card.available)}
                </td>
                <td className="px-4 py-2.5 text-center text-gray-400 text-xs">
                  Dia {card.payment_day}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <span
                    className={`text-xs font-medium ${
                      card.days_to_payment <= 3
                        ? "text-red-400"
                        : card.days_to_payment <= 7
                          ? "text-yellow-400"
                          : "text-gray-400"
                    }`}
                  >
                    {card.days_to_payment}d
                  </span>
                </td>
                <td className="px-4 py-2.5 text-center">
                  <button
                    onClick={() => onPayClick(card)}
                    className="btn-ghost text-xs gap-1"
                  >
                    <Banknote className="w-3.5 h-3.5" />
                    Pague
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
